import { createGaussianData } from "./helpers/GaussianData";
import { createUniformSampler } from "./helpers/GaussianSampling";
import { byteToUnitFloat } from "./helpers/ColorUtils";
import { writeNormalizedQuaternionWXYZ } from "./helpers/QuaternionUtils";

const BYTES_PER_SPLAT = 32;

/**
 * Decodes the fixed-width 32-byte .splat format into the shared
 * Gaussian representation, uniformly sampling when a budget is set.
 */
export function loadSplat(buffer, { maxGaussians } = {}) {
  if (buffer.byteLength % BYTES_PER_SPLAT !== 0) {
    throw new Error(".splat file size is not a multiple of 32 bytes.");
  }

  const sourceCount = buffer.byteLength / BYTES_PER_SPLAT;

  if (!sourceCount) {
    throw new Error("Empty .splat file.");
  }

  const sampler = createUniformSampler(sourceCount, maxGaussians);

  const data = createGaussianData(sampler.outputCount, {
    sourceCount,
    sampled: sampler.sampled,
  });

  const view = new DataView(buffer);

  for (let outputIndex = 0; outputIndex < data.count; outputIndex++) {
    const sourceIndex = sampler.sourceIndexFor(outputIndex);

    const base = sourceIndex * BYTES_PER_SPLAT;

    const vectorOffset = outputIndex * 3;

    data.positions[vectorOffset] = view.getFloat32(base, true);

    data.positions[vectorOffset + 1] = view.getFloat32(base + 4, true);

    data.positions[vectorOffset + 2] = view.getFloat32(base + 8, true);

    data.scales[vectorOffset] = view.getFloat32(base + 12, true);

    data.scales[vectorOffset + 1] = view.getFloat32(base + 16, true);

    data.scales[vectorOffset + 2] = view.getFloat32(base + 20, true);

    data.colors[vectorOffset] = byteToUnitFloat(view.getUint8(base + 24));

    data.colors[vectorOffset + 1] = byteToUnitFloat(view.getUint8(base + 25));

    data.colors[vectorOffset + 2] = byteToUnitFloat(view.getUint8(base + 26));

    data.opacities[outputIndex] = byteToUnitFloat(view.getUint8(base + 27));

    const w = (view.getUint8(base + 28) - 128) / 128;

    const x = (view.getUint8(base + 29) - 128) / 128;

    const y = (view.getUint8(base + 30) - 128) / 128;

    const z = (view.getUint8(base + 31) - 128) / 128;

    writeNormalizedQuaternionWXYZ(data.rotations, outputIndex, w, x, y, z);
  }

  return data;
}
